// EN/DE translations for CoinArchive
export const translations = {
  en: {
    nav: {
      coins: "Coins",
      countries: "Countries",
      series: "Series",
      learn: "Learn",
      search: "Search",
      account: "Account",
      cta: "Submit Coin",
    },
    hero: {
      eyebrow: "The Definitive European Archive",
      title1: "Europe’s",
      title2: "Commemorative",
      title3: "2 Euro Archive",
      sub: "A curated museum of national designs, mint marks, mintage, and series — built for collectors who read coins as history.",
      ctaPrimary: "Browse Coins",
      ctaSecondary: "Learn Collecting",
      scroll: "Scroll to explore",
      statsLabel: "Archive statistics",
      plateLabel: "Plate I.",
      mintLabel: "Mint A · Berlin",
    },
    home: {
      archive: {
        eyebrow: "Archive overview",
        meta: "4 collections",
        title: "Explore the archive",
        sub: "Navigate coins, countries, series, and mint marks — the four pillars of every serious 2 Euro collection.",
        cards: [
          { key: "coins", to: "/coins", eyebrow: "Catalogue", title: "Coins", desc: "Browse the full archive with filters for country, year, series, and mint.", cta: "Browse coins" },
          { key: "countries", to: "/countries", eyebrow: "Member states", title: "Countries", desc: "Discover issuing nations, timelines, and national design traditions.", cta: "View countries" },
          { key: "series", to: "/series", eyebrow: "Curated sets", title: "Series", desc: "Follow multi-year programmes from federal states to joint Eurozone issues.", cta: "Explore series" },
          { key: "mintMarks", to: "/mint-marks", eyebrow: "Prägestätten", title: "Mint marks", desc: "Decode German A/D/F/G/J marks and learn how striking location shapes collecting.", cta: "Mint marks guide" },
        ],
      },
      featuredCoins: {
        eyebrow: "Featured catalogue",
        meta: "highlights",
        title: "Featured coins",
        sub: "Hand-picked commemoratives from the archive — new issues, rare strikes, and landmark designs.",
        cta: "Browse full archive",
      },
      learning: {
        eyebrow: "Collector education",
        meta: "3 guides",
        title: "Learn before you collect",
        sub: "Short, practical guides to reading coins, mint marks, and series with confidence.",
        links: [
          { key: "learn", to: "/learn", eyebrow: "Editorial", title: "Learn guide", desc: "Countries, mintage, condition, and how to read a coin detail page.", cta: "Open learn hub" },
          { key: "mintMarks", to: "/mint-marks", eyebrow: "German mints", title: "Mint marks guide", desc: "Berlin, Munich, Stuttgart, Karlsruhe, and Hamburg — what each letter means.", cta: "Read mint guide" },
          { key: "series", to: "/series", eyebrow: "Programmes", title: "Series archive", desc: "Federal states, Treaty of Rome, Erasmus, and other structured collecting paths.", cta: "View series" },
        ],
      },
      searchCta: {
        eyebrow: "Archive search",
        title: "Search the European coin archive",
        sub: "Find coins by country, year, designer, series, or theme — across the full catalogue.",
        button: "Open search",
        fallback: "Browse all coins",
        hint: "Tip: press Ctrl+K or ⌘K anywhere on the site",
      },
      trust: {
        eyebrow: "Archive quality",
        meta: "Built to last",
        title: "An archive you can trust",
        sub: "CoinArchive is structured like a numismatic reference — curated entries, clear taxonomy, and room to grow with contributors.",
        pillars: [
          { num: "01", title: "Curated archive", body: "Each coin entry is organized for reading — story, specs, mintage, and related issues in one place." },
          { num: "02", title: "Official-source friendly", body: "Data fields align with national mint publications and catalogue conventions collectors already use." },
          { num: "03", title: "Structured taxonomy", body: "Country, year, series, and mint mark filters keep large catalogues navigable as the archive grows." },
          { num: "04", title: "Contributor-ready", body: "Built for future submissions — corrections, missing coins, and community contributions without losing structure." },
        ],
      },
    },
    manifesto: {
      small: "Manifesto",
      text: "Every commemorative 2 Euro coin tells a story of the country that issued it — a moment cast in metal,",
      highlightText: "a memory pressed into circulation.",
      attribution: "— Coin Archive · Curatorial Statement",
    },
    feature: {
      eyebrow: "Featured Story",
      country: "Germany",
      year: "2019",
      title: "30 Years — Fall of the Berlin Wall",
      body: "Issued in 2019, this coin marks three decades since the wall came down. Its design weaves together rising hands and the silhouette of the Brandenburg Gate — a monument to peaceful revolution and the reunification of Europe.",
      cta: "Read Story",
      meta: "Designed by Bodo Broschat · Mintage 30,000,000",
    },
    countries: {
      eyebrow: "Explore",
      title: "By Country",
      sub: "Each member state of the Eurozone issues its own commemorative coins. Discover the archive by nation.",
      count: "coins",
    },
    timeline: {
      eyebrow: "Timeline",
      title: "Commemorative Europe",
      sub: "From the first national issues in 2004 to the latest mintings — a continent told through coinage.",
    },
    mints: {
      eyebrow: "Mint Marks",
      title: "Mint Marks",
      sub: "Explore the German mint marks behind every coin.",
      meta: "Federal Republic of Germany",
    },
    stats: {
      eyebrow: "Archive in Numbers",
      coins: "Catalogued Coins",
      countries: "Member States",
      years: "Years of History",
    },
    recent: {
      eyebrow: "Recently Catalogued",
      title: "Latest Additions",
      sub: "New entries continuously verified by our editorial team.",
    },
    contribute: {
      eyebrow: "Contribute",
      title: "Help preserve Europe’s coin history.",
      sub: "Submit missing coins, improve data and contribute to the growing archive.",
      cta: "Submit Coin",
    },
    footer: {
      tagline: "A digital archive dedicated to the commemorative 2 Euro coins of Europe.",
      explore: "Explore",
      about: "About",
      legal: "Legal",
      coins: "Coins",
      countries: "Countries",
      series: "Series",
      learn: "Learn",
      aboutLink: "About",
      contact: "Contact",
      contribute: "Contribute",
      privacy: "Privacy",
      imprint: "Imprint",
      copyright: "© 2025 CoinArchive. All rights reserved.",
    },
    coins: {
      title: "The Coin Archive",
      eyebrow: "Catalogue",
      sub: "Browse the complete archive of European 2 Euro commemorative coins. Filter by country, year and mint.",
      searchPlaceholder: "Search coins, themes, designers…",
      filterByCountry: "Country",
      filterByYear: "Year",
      filterByMint: "Mint",
      allCountries: "All Countries",
      allYears: "All Years",
      allMints: "All Mints",
      sortBy: "Sort",
      sortNewest: "Newest first",
      sortOldest: "Oldest first",
      sortCountry: "By country",
      sortRarity: "Rarity first",
      resultsCount: "coins",
      empty: "No coins match your filters.",
      clear: "Clear filters",
      designer: "Designer",
      mintage: "Mintage",
    },
    detail: {
      back: "Back to archive",
      breadcrumb: "Coins",
      obverse: "Obverse",
      reverse: "Reverse",
      story: "Story",
      specs: "Specifications",
      designer: "Designer",
      mintage: "Mintage",
      diameter: "Diameter",
      weight: "Weight",
      thickness: "Thickness",
      composition: "Composition",
      edge: "Edge",
      mint: "Mint",
      country: "Country",
      year: "Year",
      series: "Series",
      plate: "Plate",
      related: "More from this country",
      submitImprove: "Suggest a correction",
      notFound: "Coin not found.",
      gallery: "Gallery",
      releaseDate: "Release Date",
      value: "Face Value",
      coinType: "Coin Type",
      historical: "Historical Background",
      obverseDesc: "Obverse",
      reverseDesc: "Reverse",
      collectorNotes: "Collector notes",
      coinCode: "Catalogue code",
      notRecorded: "Not recorded yet",
      noHistorical: "Historical background not recorded yet.",
    },
    countriesPage: {
      title: "Countries of the Archive",
      eyebrow: "Member States",
      sub: "Every Eurozone country issues its own commemorative 2 Euro coins. Browse the archive nation by nation.",
      since: "Issuing since",
      coinsCount: "coins catalogued",
      empty: "No countries in the archive yet.",
    },
    countryDetail: {
      eyebrow: "Country",
      coinsHere: "Coins from",
      mintsHere: "Mint marks",
      timeline: "Timeline",
      stats: { coins: "Coins", years: "Years issuing", series: "Series" },
    },
    seriesPage: {
      title: "Coin Series",
      eyebrow: "Curated collections",
      sub: "From the German federal states to joint Eurozone issues — every commemorative belongs to a series.",
      coinsCount: "coins",
      yearRange: "Year range",
      empty: "No series in the archive yet.",
    },
    seriesDetail: {
      eyebrow: "Series",
      coinsInSeries: "Coins in this series",
      timeline: "Timeline",
    },
    learn: {
      title: "Learn 2 Euro Commemorative Coins",
      eyebrow: "Educational hub",
      sub: "A collector-friendly guide to countries, mint marks, mintage, series, and coin condition.",
      featuredTitle: "Featured learning guides",
      editorialTitle: "Editorial deep dives",
      readGuide: "Read guide",
      readTime: "min read",
      cards: [
        { slug: "what-are-2-euro", category: "Basics", title: "What are 2 Euro commemorative coins?", excerpt: "Legal tender with a story — how national themes meet a shared European denomination.", readTime: 6, href: "#commemorative-explained" },
        { slug: "mint-marks-work", category: "Reading the coin", title: "How mint marks work", excerpt: "The small letter on German coins reveals which mint struck your piece — and why collectors compare variants.", readTime: 4, href: "/mint-marks" },
        { slug: "mintage", category: "Rarity", title: "Understanding mintage", excerpt: "Production numbers shape scarcity. Learn why two nearly identical coins can differ sharply in demand.", readTime: 5, href: "#mintage-rarity" },
        { slug: "condition", category: "Practice", title: "Coin condition basics", excerpt: "From circulation wear to proof finishes — the vocabulary that helps you judge and store coins responsibly.", readTime: 7, href: "#collector-condition" },
        { slug: "coin-detail", category: "Archive", title: "How to read a coin detail page", excerpt: "Navigate obverse, reverse, specs, mintage, and related coins like a curator reading the archive.", readTime: 4, href: "#country-year-series" },
        { slug: "collecting-safely", category: "Practice", title: "Collecting safely", excerpt: "Storage, handling, sourcing, and documentation habits that protect both value and enjoyment.", readTime: 6, href: "#collector-condition" },
      ],
      guides: [
        {
          id: "commemorative-explained",
          label: "I · Foundations",
          title: "2 Euro commemorative coins explained",
          paragraphs: [
            "Commemorative 2 Euro coins are legal-tender pieces issued by Eurozone member states to mark anniversaries, cultural milestones, and shared European themes. They circulate at face value while carrying designs that tell national and continental stories.",
            "Most issues retain the common European map obverse and replace the national side with a commemorative motif. Joint programmes — such as the Treaty of Rome or Erasmus series — unite multiple countries under one visual theme for a single year.",
            "For collectors, the appeal lies in design quality, historical context, mint variants, and mintage. CoinArchive catalogues each entry with country, year, series, and striking details so you can compare issues across the archive.",
          ],
        },
        {
          id: "country-year-series",
          label: "II · Context",
          title: "Why country, year, and series matter",
          paragraphs: [
            "Country identifies the issuing state and its design tradition. Year anchors the coin to a historical moment — a anniversary, Olympic Games, or EU programme. Series groups related issues, such as Germany’s federal states cycle or multi-nation joint commemoratives.",
            "On a coin detail page, start with the title and country breadcrumb, then read the story tab for historical background. Check specs for diameter, composition, and edge inscription. Use related coins to see what else was issued from the same nation or programme.",
            "Filtering the archive by country, year, or series helps you build focused sets. A complete federal-states run, for example, is defined by series membership rather than date alone.",
          ],
        },
        {
          id: "mintage-rarity",
          label: "III · Scarcity",
          title: "Mintage and rarity",
          paragraphs: [
            "Mintage is the number of coins struck for circulation or collector demand. Lower figures do not always mean high market value, but they strongly influence scarcity — especially when a design is popular or a mint mark variant is limited.",
            "German coins split production across five mints (A, D, F, G, J), so total mintage is distributed unevenly. Some mint-letter combinations for a given year can be noticeably harder to find in uncirculated condition.",
            "Treat mintage as one signal among many: condition, demand, and whether a coin was widely hoarded or spent all affect real-world rarity. CoinArchive surfaces official figures where available so you can compare at a glance.",
          ],
        },
        {
          id: "obverse-reverse-edge",
          label: "IV · Anatomy",
          title: "Obverse, reverse, edge inscription",
          paragraphs: [
            "The obverse typically shows the shared European design — a map of Europe with denomination. The reverse carries the national commemorative artwork, often with the issuing country’s name and the theme in the local language.",
            "Edge inscriptions vary by country and issue: reeded edges, incused lettering, or stars may appear. On German coins, the mint mark letter sits near the bottom of the national side — a key detail when comparing Berlin, Munich, or Hamburg strikes.",
            "When photographing or storing coins, capture both faces and note edge features. Subtle die differences and finish quality are easier to assess with good light and minimal handling.",
          ],
        },
        {
          id: "collector-condition",
          label: "V · Practice",
          title: "Collector notes and condition",
          paragraphs: [
            "Handle coins by the rim, store them in inert holders or capsules, and keep humidity stable. Avoid cleaning — surface disturbance can permanently reduce appeal to collectors and graders.",
            "Learn basic grade terms: uncirculated (no wear from circulation), proof ( specially struck for collectors ), and circulation strikes ( intended for everyday use ). Document provenance when buying from dealers or online markets.",
            "Collecting safely also means verifying listings against archive data: country, year, mint mark, and mintage. Cross-check unusual bargains, prefer reputable sellers, and note imperfections before they become costly surprises.",
          ],
        },
      ],
      cta: {
        eyebrow: "Continue exploring",
        title: "Put the guide into practice",
        sub: "Browse catalogued coins or dive deeper into German mint marks.",
        browseArchive: "Browse the archive",
        exploreMintMarks: "Explore mint marks",
      },
      articles: [
        { slug: "what-are-2-euro", category: "Basics", title: "What are 2 Euro commemorative coins?", excerpt: "Legal tender with a story — how national themes meet a shared European denomination.", readTime: 6 },
        { slug: "mint-marks", category: "Reading the coin", title: "Mint marks explained", excerpt: "The small letter on German coins reveals which mint struck your piece.", readTime: 4 },
        { slug: "mintage", category: "Rarity", title: "Mintage explained", excerpt: "Production numbers shape scarcity and collector demand.", readTime: 5 },
        { slug: "collecting", category: "Practice", title: "A beginner's guide to collecting", excerpt: "How to start a serious 2 Euro collection — storage, sourcing, and condition.", readTime: 9 },
        { slug: "condition", category: "Practice", title: "Coin condition guide", excerpt: "From Uncirculated to Proof — grading vocabulary that affects value.", readTime: 7 },
      ],
    },
    mintMarks: {
      title: "Mint Marks Guide",
      eyebrow: "Reading the maker",
      sub: "Learn how mint marks identify where a coin was struck.",
      gridTitle: "German mint marks",
      gridSub: "Five federal mints share Germany’s commemorative production. Each letter marks the city of striking.",
      mapTitle: "Federal Republic of Germany",
      markExplanations: {
        A: "Berlin strikes often appear in eastern distribution channels. Collectors track A-mint pieces when building complete year sets.",
        D: "Munich (Bayerisches Hauptmünzamt) is a high-volume mint. D marks are common in southern Germany and widely seen in circulation.",
        F: "Stuttgart serves Baden-Württemberg. F and G mints sometimes confuse beginners — compare the letter carefully near the rim.",
        G: "Karlsruhe shares the Baden-Württemberg mint network with Stuttgart. G variants can differ in relative scarcity by year.",
        J: "Hamburg strikes carry the J mark. Northern collectors often notice J-mint coins in regional rolls and bank bundles.",
      },
      explanationTitle: "Understanding mint marks",
      explanation: [
        {
          title: "What is a mint mark?",
          body: "A mint mark is a small symbol — on German 2 Euro coins, a single letter — indicating which mint facility produced the piece. It is struck into the design, not added later, and helps authenticate origin.",
        },
        {
          title: "Why German 2 Euro coins can have A/D/F/G/J variants",
          body: "Germany distributes coin production across five state mints to meet national demand. The same commemorative design may therefore exist in five mint-mark variants, each with its own production share for that year.",
        },
        {
          title: "How mint marks affect collecting",
          body: "Completing all five letters for a given year is a classic German collector goal. Scarcer mint splits, condition, and thematic popularity can make one letter more sought after than others — even when face value stays €2.",
        },
      ],
      workflowTitle: "Example collector workflow",
      workflow: [
        { step: "01", title: "Choose country", body: "Confirm the issuing state — mint letters apply to German national sides." },
        { step: "02", title: "Check year", body: "Select the issue year to compare mint splits and mintage totals." },
        { step: "03", title: "Compare mint mark", body: "Locate the letter (A, D, F, G, J) and match it to the city grid." },
        { step: "04", title: "Check mintage", body: "Review production figures and how they divide across mints." },
        { step: "05", title: "Save notes", body: "Record condition, source, and gaps in your set for future hunts." },
      ],
      otherCountries: "Future countries",
      otherCountriesNote: "Other euro countries may use different mint mark systems. CoinArchive is structured to support them later.",
    },
    search: {
      placeholder: "Search coins, countries, designers, series…",
      hintKey: "Press Esc to close",
      empty: "No results. Try a country code (DE, FR…), a year (2019), or a designer name.",
      results: "results",
    },
  },
  de: {
    nav: {
      coins: "Münzen",
      countries: "Länder",
      series: "Serien",
      learn: "Wissen",
      search: "Suche",
      account: "Konto",
      cta: "Münze einreichen",
    },
    hero: {
      eyebrow: "Das Europäische Referenzarchiv",
      title1: "Europas",
      title2: "Gedenkmünzen",
      title3: "2-Euro-Archiv",
      sub: "Ein kuratiertes Museum nationaler Motive, Prägezeichen, Auflagen und Serien — für Sammler, die Münzen als Geschichte lesen.",
      ctaPrimary: "Münzen durchsuchen",
      ctaSecondary: "Sammeln lernen",
      scroll: "Scrollen",
      statsLabel: "Archivstatistik",
      plateLabel: "Tafel I.",
      mintLabel: "Prägezeichen A · Berlin",
    },
    home: {
      archive: {
        eyebrow: "Archivüberblick",
        meta: "4 Sammlungen",
        title: "Das Archiv erkunden",
        sub: "Münzen, Länder, Serien und Prägezeichen — die vier Säulen jeder ernsthaften 2-Euro-Sammlung.",
        cards: [
          { key: "coins", to: "/coins", eyebrow: "Katalog", title: "Münzen", desc: "Vollständiges Archiv mit Filtern für Land, Jahr, Serie und Prägezeichen.", cta: "Münzen ansehen" },
          { key: "countries", to: "/countries", eyebrow: "Mitgliedsstaaten", title: "Länder", desc: "Ausgebende Nationen, Zeitstrahlen und nationale Designtraditionen.", cta: "Länder ansehen" },
          { key: "series", to: "/series", eyebrow: "Kuratierte Sets", title: "Serien", desc: "Mehrjährige Programme von Bundesländern bis zu gemeinsamen Euro-Ausgaben.", cta: "Serien entdecken" },
          { key: "mintMarks", to: "/mint-marks", eyebrow: "Prägestätten", title: "Prägezeichen", desc: "Deutsche A/D/F/G/J-Zeichen und warum die Prägestätte fürs Sammeln zählt.", cta: "Prägezeichen-Leitfaden" },
        ],
      },
      featuredCoins: {
        eyebrow: "Katalog-Highlights",
        meta: "Auswahl",
        title: "Empfohlene Münzen",
        sub: "Handverlesene Gedenkmünzen — neue Ausgaben, seltene Prägungen und Meilenstein-Designs.",
        cta: "Gesamtes Archiv durchsuchen",
      },
      learning: {
        eyebrow: "Sammler-Wissen",
        meta: "3 Leitfäden",
        title: "Lernen, bevor du sammelst",
        sub: "Kurze, praxisnahe Guides zum Lesen von Münzen, Prägezeichen und Serien.",
        links: [
          { key: "learn", to: "/learn", eyebrow: "Redaktion", title: "Wissens-Hub", desc: "Länder, Auflagen, Erhaltung und Münzdetailseiten verstehen.", cta: "Wissens-Hub öffnen" },
          { key: "mintMarks", to: "/mint-marks", eyebrow: "Deutsche Prägung", title: "Prägezeichen-Leitfaden", desc: "Berlin, München, Stuttgart, Karlsruhe und Hamburg — was jeder Buchstabe bedeutet.", cta: "Leitfaden lesen" },
          { key: "series", to: "/series", eyebrow: "Programme", title: "Serien-Archiv", desc: "Bundesländer, Römische Verträge, Erasmus und weitere Sammelpfade.", cta: "Serien ansehen" },
        ],
      },
      searchCta: {
        eyebrow: "Archivsuche",
        title: "Das europäische Münzarchiv durchsuchen",
        sub: "Münzen nach Land, Jahr, Künstler, Serie oder Thema im gesamten Katalog finden.",
        button: "Suche öffnen",
        fallback: "Alle Münzen ansehen",
        hint: "Tipp: Ctrl+K oder ⌘K drücken",
      },
      trust: {
        eyebrow: "Archivqualität",
        meta: "Nachhaltig aufgebaut",
        title: "Ein Archiv, dem du vertrauen kannst",
        sub: "CoinArchive ist wie ein numismatisches Nachschlagewerk — kuratierte Einträge, klare Taxonomie und Raum für Mitwirkende.",
        pillars: [
          { num: "01", title: "Kuratiertes Archiv", body: "Jeder Münzeintrag ist zum Lesen organisiert — Story, Spezifikationen, Auflage und verwandte Ausgaben." },
          { num: "02", title: "Offizielle Quellen", body: "Datenfelder orientieren sich an nationalen Münzamt-Publikationen und etablierten Katalogkonventionen." },
          { num: "03", title: "Strukturierte Taxonomie", body: "Filter für Land, Jahr, Serie und Prägezeichen halten große Kataloge navigierbar." },
          { num: "04", title: "Mitwirkende-ready", body: "Vorbereitet für Einreichungen — Korrekturen, fehlende Münzen und Community-Beiträge ohne Strukturverlust." },
        ],
      },
    },
    manifesto: {
      small: "Manifest",
      text: "Jede 2-Euro-Gedenkmünze erzählt eine Geschichte des Landes, das sie ausgegeben hat — ein Moment, in Metall gegossen,",
      highlightText: "eine Erinnerung im Umlauf.",
      attribution: "— Coin Archive · Kuratorische Leitlinie",
    },
    feature: {
      eyebrow: "Geschichte des Monats",
      country: "Deutschland",
      year: "2019",
      title: "30 Jahre Mauerfall",
      body: "Die Ausgabe von 2019 erinnert an drei Jahrzehnte seit dem Fall der Berliner Mauer. Erhobene Hände und die Silhouette des Brandenburger Tors verweben sich zu einem Denkmal der friedlichen Revolution und der Wiedervereinigung Europas.",
      cta: "Geschichte lesen",
      meta: "Entwurf: Bodo Broschat · Auflage 30.000.000",
    },
    countries: {
      eyebrow: "Entdecken",
      title: "Nach Land",
      sub: "Jeder Mitgliedstaat der Eurozone gibt eigene Gedenkmünzen aus. Entdecke das Archiv nach Nation.",
      count: "Münzen",
    },
    timeline: {
      eyebrow: "Zeitstrahl",
      title: "Gedenkmünzen Europas",
      sub: "Von den ersten nationalen Ausgaben 2004 bis zu den jüngsten Prägungen — ein Kontinent in Münzen erzählt.",
    },
    mints: {
      eyebrow: "Prägezeichen",
      title: "Prägezeichen",
      sub: "Entdecke die deutschen Prägezeichen hinter jeder Münze.",
      meta: "Bundesrepublik Deutschland",
    },
    stats: {
      eyebrow: "Archiv in Zahlen",
      coins: "Katalogisierte Münzen",
      countries: "Mitgliedstaaten",
      years: "Jahre Geschichte",
    },
    recent: {
      eyebrow: "Neu katalogisiert",
      title: "Aktuelle Zugänge",
      sub: "Neue Einträge werden laufend von der Redaktion verifiziert.",
    },
    contribute: {
      eyebrow: "Mitmachen",
      title: "Hilf, Europas Münzgeschichte zu bewahren.",
      sub: "Reiche fehlende Münzen ein, verbessere Daten und trage zum wachsenden Archiv bei.",
      cta: "Münze einreichen",
    },
    footer: {
      tagline: "Ein digitales Archiv der 2-Euro-Gedenkmünzen Europas.",
      explore: "Entdecken",
      about: "Über",
      legal: "Rechtliches",
      coins: "Münzen",
      countries: "Länder",
      series: "Serien",
      learn: "Wissen",
      aboutLink: "Über uns",
      contact: "Kontakt",
      contribute: "Mitmachen",
      privacy: "Datenschutz",
      imprint: "Impressum",
      copyright: "© 2025 CoinArchive. Alle Rechte vorbehalten.",
    },
    coins: {
      title: "Das Münzarchiv",
      eyebrow: "Katalog",
      sub: "Durchsuche das vollständige Archiv der europäischen 2-Euro-Gedenkmünzen. Filtere nach Land, Jahr und Münzstätte.",
      searchPlaceholder: "Münzen, Themen, Künstler suchen…",
      filterByCountry: "Land",
      filterByYear: "Jahr",
      filterByMint: "Münzstätte",
      allCountries: "Alle Länder",
      allYears: "Alle Jahre",
      allMints: "Alle Prägestätten",
      sortBy: "Sortieren",
      sortNewest: "Neueste zuerst",
      sortOldest: "Älteste zuerst",
      sortCountry: "Nach Land",
      sortRarity: "Seltenheit zuerst",
      resultsCount: "Münzen",
      empty: "Keine Münzen entsprechen deinen Filtern.",
      clear: "Filter zurücksetzen",
      designer: "Entwurf",
      mintage: "Auflage",
    },
    detail: {
      back: "Zurück zum Archiv",
      breadcrumb: "Münzen",
      obverse: "Vorderseite",
      reverse: "Rückseite",
      story: "Geschichte",
      specs: "Spezifikationen",
      designer: "Entwurf",
      mintage: "Auflage",
      diameter: "Durchmesser",
      weight: "Gewicht",
      thickness: "Dicke",
      composition: "Zusammensetzung",
      edge: "Rand",
      mint: "Münzstätte",
      country: "Land",
      year: "Jahr",
      series: "Serie",
      plate: "Tafel",
      related: "Weitere aus diesem Land",
      submitImprove: "Korrektur vorschlagen",
      notFound: "Münze nicht gefunden.",
      gallery: "Galerie",
      releaseDate: "Ausgabedatum",
      value: "Nennwert",
      coinType: "Münzart",
      historical: "Historischer Hintergrund",
      obverseDesc: "Vorderseite",
      reverseDesc: "Rückseite",
      collectorNotes: "Sammlerhinweise",
      coinCode: "Katalogcode",
      notRecorded: "Noch nicht erfasst",
      noHistorical: "Historischer Hintergrund noch nicht erfasst.",
    },
    countriesPage: {
      title: "Länder des Archivs",
      eyebrow: "Mitgliedstaaten",
      sub: "Jedes Eurozonen-Land gibt eigene 2-Euro-Gedenkmünzen aus. Durchsuche das Archiv Land für Land.",
      since: "Gibt aus seit",
      coinsCount: "Münzen katalogisiert",
      empty: "Noch keine Länder im Archiv.",
    },
    countryDetail: {
      eyebrow: "Land",
      coinsHere: "Münzen aus",
      mintsHere: "Prägezeichen",
      timeline: "Zeitstrahl",
      stats: { coins: "Münzen", years: "Jahre Ausgabe", series: "Serien" },
    },
    seriesPage: {
      title: "Münzserien",
      eyebrow: "Kuratierte Sammlungen",
      sub: "Von den deutschen Bundesländern bis zu gemeinschaftlichen Eurozonen-Ausgaben.",
      coinsCount: "Münzen",
      yearRange: "Zeitraum",
      empty: "Noch keine Serien im Archiv.",
    },
    seriesDetail: {
      eyebrow: "Serie",
      coinsInSeries: "Münzen dieser Serie",
      timeline: "Zeitstrahl",
    },
    learn: {
      title: "2-Euro-Gedenkmünzen verstehen",
      eyebrow: "Wissenszentrum",
      sub: "Ein sammlerfreundlicher Leitfaden zu Ländern, Prägezeichen, Auflagen, Serien und Erhaltung.",
      featuredTitle: "Empfohlene Lernkarten",
      editorialTitle: "Redaktionelle Vertiefungen",
      readGuide: "Leitfaden lesen",
      readTime: "Min. Lesezeit",
      cards: [
        { slug: "what-are-2-euro", category: "Grundlagen", title: "Was sind 2-Euro-Gedenkmünzen?", excerpt: "Gesetzliches Zahlungsmittel mit Geschichte — wie nationale Motive auf einer gemeinsamen Nominale treffen.", readTime: 6, href: "#commemorative-explained" },
        { slug: "mint-marks-work", category: "Münze lesen", title: "So funktionieren Prägezeichen", excerpt: "Der kleine Buchstabe auf deutschen Münzen verrät die Prägestätte — und warum Sammler Varianten vergleichen.", readTime: 4, href: "/mint-marks" },
        { slug: "mintage", category: "Seltenheit", title: "Auflagen verstehen", excerpt: "Stückzahlen prägen Seltenheit. Warum zwei ähnliche Münzen sehr unterschiedlich begehrt sein können.", readTime: 5, href: "#mintage-rarity" },
        { slug: "condition", category: "Praxis", title: "Erhaltungsgrad-Grundlagen", excerpt: "Von Umlaufsprägung bis Polierte Platte — Begriffe für verantwortungsvolles Bewerten und Lagern.", readTime: 7, href: "#collector-condition" },
        { slug: "coin-detail", category: "Archiv", title: "Münzdetailseite lesen", excerpt: "Vorderseite, Rückseite, Spezifikationen, Auflage und verwandte Münzen wie ein Kurator nutzen.", readTime: 4, href: "#country-year-series" },
        { slug: "collecting-safely", category: "Praxis", title: "Sicher sammeln", excerpt: "Lagerung, Handhabung, Bezugsquellen und Dokumentation — Gewohnheiten, die Wert und Freude schützen.", readTime: 6, href: "#collector-condition" },
      ],
      guides: [
        {
          id: "commemorative-explained",
          label: "I · Grundlagen",
          title: "2-Euro-Gedenkmünzen erklärt",
          paragraphs: [
            "2-Euro-Gedenkmünzen sind gesetzliche Zahlungsmittel, die Eurozonen-Staaten zu Jubiläen, Kultur und europäischen Themen ausgeben. Sie zirkulieren zum Nennwert und erzählen nationale und kontinentale Geschichten.",
            "Die meisten Ausgaben behalten die gemeinsame Europakarte auf der Vorderseite und ersetzen die nationale Seite durch ein Gedenkmotiv. Gemeinsame Programme vereinen mehrere Länder unter einem visuellen Thema.",
            "Für Sammler zählen Design, historischer Kontext, Prägevarianten und Auflage. CoinArchive katalogisiert Land, Jahr, Serie und Prägedetails für den Vergleich im Archiv.",
          ],
        },
        {
          id: "country-year-series",
          label: "II · Kontext",
          title: "Warum Land, Jahr und Serie wichtig sind",
          paragraphs: [
            "Das Land bestimmt den ausgebenden Staat und seine Designtradition. Das Jahr verankert die Münze historisch. Serien fassen verwandte Ausgaben zusammen — etwa die deutsche Bundesländer-Reihe.",
            "Auf der Detailseite beginnst du mit Titel und Land, liest die Story für den Hintergrund und prüfst Spezifikationen sowie Randinschrift. Verwandte Münzen zeigen weitere Ausgaben desselben Staates oder Programms.",
            "Filter nach Land, Jahr oder Serie helfen beim gezielten Sammeln. Eine vollständige Bundesländer-Serie definiert sich über die Serie, nicht nur über das Datum.",
          ],
        },
        {
          id: "mintage-rarity",
          label: "III · Seltenheit",
          title: "Auflage und Seltenheit",
          paragraphs: [
            "Die Auflage ist die Anzahl geprägter Stücke. Niedrige Zahlen bedeuten nicht immer hohen Marktwert, prägen aber die Seltenheit — besonders bei beliebten Motiven oder seltenen Prägezeichen.",
            "Deutsche Münzen verteilen sich auf fünf Prägestätten (A, D, F, G, J). Die Gesamtauflage ist ungleich verteilt; manche Buchstaben-Jahres-Kombinationen sind schwerer in Stempelglanz zu finden.",
            "Sieh die Auflage als eines von mehreren Signalen: Erhaltung, Nachfrage und ob Münzen gehortet oder ausgegeben wurden, beeinflussen die reale Seltenheit.",
          ],
        },
        {
          id: "obverse-reverse-edge",
          label: "IV · Anatomie",
          title: "Vorderseite, Rückseite, Randinschrift",
          paragraphs: [
            "Die Vorderseite zeigt meist das gemeinsame Europadesign mit Nennwert. Die Rückseite trägt das nationale Gedenkmotiv, oft mit Ländername und Thema in Landessprache.",
            "Randinschriften variieren: Riffelung, eingeprägte Schrift oder Sterne. Bei deutschen Münzen sitzt das Prägezeichen unten an der nationalen Seite — entscheidend beim Vergleich der Prägestätten.",
            "Fotografiere beide Seiten und notiere Randmerkmale. Feine Unterschiede erkennt man bei gutem Licht und minimaler Handhabung.",
          ],
        },
        {
          id: "collector-condition",
          label: "V · Praxis",
          title: "Sammlerhinweise und Erhaltung",
          paragraphs: [
            "Fasse Münzen am Rand an, lagere sie in neutralen Hüllen oder Kapseln und halte die Luftfeuchtigkeit stabil. Reinige nicht — Oberflächenschäden mindern dauerhaft den Sammlerwert.",
            "Lerne Grundbegriffe: Stempelglanz, Polierte Platte und Umlaufsprägung. Dokumentiere Herkunft bei Händlern und Online-Käufen.",
            "Sicher sammeln heiß auch: Angebote mit Archivdaten abgleichen — Land, Jahr, Prägezeichen, Auflage. Ungewöhnliche Schnäppchen kritisch prüfen und Mängel früh notieren.",
          ],
        },
      ],
      cta: {
        eyebrow: "Weiter erkunden",
        title: "Leitfaden in die Praxis umsetzen",
        sub: "Durchsuche katalogisierte Münzen oder vertiefe dich in deutsche Prägezeichen.",
        browseArchive: "Archiv durchsuchen",
        exploreMintMarks: "Prägezeichen entdecken",
      },
      articles: [
        { slug: "what-are-2-euro", category: "Grundlagen", title: "Was sind 2-Euro-Gedenkmünzen?", excerpt: "Gesetzliches Zahlungsmittel mit Geschichte.", readTime: 6 },
        { slug: "mint-marks", category: "Münze lesen", title: "Prägezeichen erklärt", excerpt: "Der Buchstabe auf deiner Münze verrät die Prägestätte.", readTime: 4 },
        { slug: "mintage", category: "Seltenheit", title: "Auflage erklärt", excerpt: "Stückzahlen prägen Seltenheit und Nachfrage.", readTime: 5 },
        { slug: "collecting", category: "Praxis", title: "Sammler-Einsteigerleitfaden", excerpt: "Wie du eine ernsthafte 2-Euro-Sammlung beginnst.", readTime: 9 },
        { slug: "condition", category: "Praxis", title: "Erhaltungsgrad-Leitfaden", excerpt: "Von Stempelglanz bis Polierte Platte.", readTime: 7 },
      ],
    },
    mintMarks: {
      title: "Prägezeichen-Leitfaden",
      eyebrow: "Die Münzstätte lesen",
      sub: "Erfahre, wie Prägezeichen die Prägestätte einer Münze identifizieren.",
      gridTitle: "Deutsche Prägezeichen",
      gridSub: "Fünf Bundesmünzstätten teilen sich die deutsche Gedenkmünzen-Produktion. Jeder Buchstabe steht für die Prägestadt.",
      mapTitle: "Bundesrepublik Deutschland",
      markExplanations: {
        A: "Berliner Prägungen erscheinen oft im Osten. Sammler verfolgen A-Stücke beim Aufbau kompletter Jahressätze.",
        D: "München ist eine Hochvolumen-Prägestätte. D-Marken sind im Süden häufig in Umlauf.",
        F: "Stuttgart bedient Baden-Württemberg. F und G verwechseln Einsteiger oft — Buchstabe am Rand genau prüfen.",
        G: "Karlsruhe teilt das baden-württembergische Netz mit Stuttgart. G-Varianten können je nach Jahr unterschiedlich selten sein.",
        J: "Hamburg trägt das J-Zeichen. Im Norden findet man J-Prägungen oft in Rollen und Bankposten.",
      },
      explanationTitle: "Prägezeichen verstehen",
      explanation: [
        {
          title: "Was ist ein Prägezeichen?",
          body: "Ein Prägezeichen — bei deutschen 2-Euro-Münzen ein Buchstabe — zeigt, welche Münzstätte das Stück produziert hat. Es ist Teil des Prägebildes, nicht nachträglich aufgebracht.",
        },
        {
          title: "Warum deutsche 2-Euro-Münzen A/D/F/G/J haben",
          body: "Deutschland verteilt die Produktion auf fünf Staatsmünzen. Dasselbe Gedenkmotiv kann in fünf Prägezeichen-Varianten existieren — mit unterschiedlichen Stückzahlen pro Jahr.",
        },
        {
          title: "Einfluss auf das Sammeln",
          body: "Alle fünf Buchstaben eines Jahres zu vervollständigen ist ein klassisches deutsches Sammlerziel. Seltenere Verteilungen, Erhaltung und Thema können einen Buchstaben begehrter machen — bei €2 Nennwert.",
        },
      ],
      workflowTitle: "Beispiel-Sammlerworkflow",
      workflow: [
        { step: "01", title: "Land wählen", body: "Ausgebenden Staat bestätigen — Buchstaben gelten für die deutsche nationale Seite." },
        { step: "02", title: "Jahr prüfen", body: "Ausgabejahr wählen, um Prägeverteilung und Auflagen zu vergleichen." },
        { step: "03", title: "Prägezeichen vergleichen", body: "Buchstabe (A, D, F, G, J) finden und der Stadt zuordnen." },
        { step: "04", title: "Auflage prüfen", body: "Stückzahlen und Aufteilung auf die Prägestätten ansehen." },
        { step: "05", title: "Notizen speichern", body: "Erhaltung, Quelle und Lücken im Satz für spätere Suche dokumentieren." },
      ],
      otherCountries: "Weitere Länder",
      otherCountriesNote: "Andere Euro-Länder nutzen andere Prägezeichen-Systeme. CoinArchive ist darauf vorbereitet, sie später zu unterstützen.",
    },
    search: {
      placeholder: "Münzen, Länder, Künstler, Serien suchen…",
      hintKey: "Esc zum Schliessen",
      empty: "Keine Treffer. Probiere einen Ländercode, ein Jahr oder einen Künstlernamen.",
      results: "Treffer",
    },
  },
};
